import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, Badge, Field, SectionTitle } from "@/components/ui-hud";
import { Modal } from "@/pages/Contracts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Plane, Wrench, MapPin, Upload, X as XIcon } from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

export default function Marketplace() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "plane", title: "", price: "", currency: "USD", location: "", year: "", hours: "", condition: "", description: "", image_url: "" });
  const [uploading, setUploading] = useState(false);

  const load = () => {
    const url = filter === "all" ? "/marketplace" : `/marketplace?kind=${filter}`;
    api.get(url).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("pjp_token");
      const res = await fetch(`${API}/images/upload`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: fullUrl }));
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/marketplace", { ...form, price: parseFloat(form.price), year: form.year ? parseInt(form.year) : null, hours: form.hours ? parseInt(form.hours) : null });
      toast.success("Listing published");
      setOpen(false);
      setForm({ kind: "plane", title: "", price: "", currency: "USD", location: "", year: "", hours: "", condition: "", description: "", image_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const money = (n, c) => new Intl.NumberFormat('en-US', { style: 'currency', currency: c || 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <BackToCockpit />
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <SectionTitle overline="MARKETPLACE" title="Planes & parts." subtitle="Vetted aircraft and traceable components from operators, brokers and MROs." />
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {["all", "plane", "part"].map((k) => (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`btn-hud ${filter === k ? 'btn-hud-solid' : ''}`}>
                {k === "all" ? "ALL" : k === "plane" ? "AIRCRAFT" : "PARTS"}
              </button>
            ))}
          </div>
          {user && (
            <button data-testid="btn-post-listing" onClick={() => setOpen(true)} className="btn-hud btn-hud-solid">
              <Plus className="h-3.5 w-3.5" /> LIST
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {items.map((it) => (
          <Card key={it.id} testId={`listing-${it.id}`} className="!p-0 overflow-hidden">
            <div className="relative h-52 overflow-hidden">
              <img src={it.image_url || "https://images.unsplash.com/photo-1474302770737-173ee21bab63?w=800&q=80"} alt={it.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
              <div className="absolute top-3 left-3">
                <Badge tone={it.kind === 'plane' ? 'cyan' : 'orange'}>
                  {it.kind === 'plane' ? <Plane className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                  {it.kind.toUpperCase()}
                </Badge>
              </div>
              <div className="absolute bottom-3 right-3 font-mono text-cyan text-lg glow-cyan">{money(it.price, it.currency)}</div>
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-semibold tracking-tight mb-2">{it.title}</h3>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {it.year && <div><div className="overline">YEAR</div><div className="font-mono text-sm">{it.year}</div></div>}
                {it.hours !== null && it.hours !== undefined && <div><div className="overline">HOURS</div><div className="font-mono text-sm">{it.hours.toLocaleString()}</div></div>}
                {it.condition && <div><div className="overline">COND</div><div className="font-mono text-sm">{it.condition}</div></div>}
              </div>
              <p className="text-xs text-slate-400 line-clamp-2 mb-3">{it.description}</p>
              <div className="flex items-center justify-between">
                <div className="overline flex items-center gap-1.5"><MapPin className="h-3 w-3" />{it.location}</div>
                <div className="overline text-cyan">{it.posted_by_name}</div>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-slate-500 overline">NO LISTINGS</div>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h2 className="font-display text-2xl font-bold mb-6">New Listing</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kind">
                <select data-testid="input-kind" className="input-hud" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="plane">Aircraft</option>
                  <option value="part">Part</option>
                </select>
              </Field>
              <Field label="Price (USD)"><input data-testid="input-price" required type="number" className="input-hud" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            </div>
            <Field label="Title"><input data-testid="input-listing-title" required className="input-hud" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Year"><input data-testid="input-year" type="number" className="input-hud" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field>
              <Field label="Hours"><input data-testid="input-hours" type="number" className="input-hud" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></Field>
              <Field label="Condition"><input data-testid="input-condition" className="input-hud" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} /></Field>
            </div>
            <Field label="Location"><input data-testid="input-location" className="input-hud" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
            <Field label="Image">
              {form.image_url ? (
                <div className="relative inline-block">
                  <img src={form.image_url} alt="preview" className="h-32 border border-cyan/30 object-cover" />
                  <button type="button" data-testid="btn-image-remove" onClick={() => setForm({ ...form, image_url: "" })} className="absolute -top-2 -right-2 bg-obsidian border border-aog/60 text-aog p-1"><XIcon className="h-3 w-3" /></button>
                </div>
              ) : (
                <label data-testid="image-drop" className="flex items-center justify-center border border-dashed border-cyan/30 hover:border-cyan/60 hover:bg-cyan/5 transition-colors h-32 cursor-pointer">
                  <div className="text-center">
                    <Upload className="h-5 w-5 mx-auto text-cyan mb-2" />
                    <div className="overline">{uploading ? "UPLOADING…" : "CLICK OR DROP IMAGE · MAX 3MB"}</div>
                  </div>
                  <input data-testid="input-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
              )}
            </Field>
            <Field label="Description"><textarea data-testid="input-listing-desc" required rows={3} className="input-hud" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <button data-testid="btn-submit-listing" className="btn-hud btn-hud-solid w-full justify-center">PUBLISH</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
